/**
 * This file may contain code that uses generative AI for code assistance, unit testing and/or entire functions.
 * The generative model(s) used may be a combination of GitHub Copilot, OpenAI ChatGPT or others.
 */

import React, { useCallback, useMemo, useState } from 'react';

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { useIntl } from 'react-intl';

import FeedbackChoiceGroup from './FeedbackChoiceGroup.jsx';
import { buildFeedbackUserFeedbackText } from './buildFeedbackUserFeedbackText.js';
import { FEEDBACK_QUESTION_GROUPS } from './feedbackConstants.js';
import { getFeedbackDialogStyles } from './FeedbackDialog.styles.js';

const createEmptySelections = () =>
  FEEDBACK_QUESTION_GROUPS.reduce((acc, group) => {
    acc[group.id] = undefined;

    return acc;
  }, {});

const FeedbackDialog = ({ open, onClose, message, channelId, onSubmit }) => {
  const intl = useIntl();
  const theme = useTheme();
  const styles = useMemo(() => getFeedbackDialogStyles(theme), [theme]);
  const [feedback, setFeedback] = useState('');
  const [selections, setSelections] = useState(createEmptySelections);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const resetForm = useCallback(() => {
    setFeedback('');
    setSelections(createEmptySelections());
    setError(null);
  }, []);

  const handleClose = () => {
    if (!isSubmitting) {
      resetForm();
      onClose?.(false);
    }
  };

  const handleSelectChoice = useCallback((groupId, value) => {
    setSelections(previousSelections => ({
      ...previousSelections,
      [groupId]: value,
    }));
  }, []);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setError(null);

    const metadata = message?.metadata && typeof message.metadata === 'object' ? message.metadata : {};
    const userFeedback = buildFeedbackUserFeedbackText({
      selections,
      freeText: feedback,
      formatLabel: stringId => intl.formatMessage({ id: stringId }),
    });

    try {
      await onSubmit({
        chatChannelRef: channelId,
        messageRef: message.crn,
        rating: 'negative',
        userFeedback,
        timestamp: Date.now(),
        runRef: typeof metadata.runRef === 'string' ? metadata.runRef : undefined,
        agentRef: typeof metadata.agentRef === 'string' ? metadata.agentRef : undefined,
      });

      resetForm();
      onClose?.(true);
    } catch (err) {
      setError(err?.message || intl.formatMessage({ id: 'command_intelligence_feedback_submit_error' }));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>{intl.formatMessage({ id: 'command_intelligence_feedback_dialog_title' })}</DialogTitle>

      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {intl.formatMessage({ id: 'command_intelligence_feedback_dialog_body' })}
          </Typography>

          {message?.content || message?.text ? (
            <Box
              sx={{
                mb: 2,
                p: 2,
                borderRadius: 1,
                bgcolor: 'action.hover',
                maxHeight: 200,
                overflow: 'auto',
              }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                {intl.formatMessage({ id: 'command_intelligence_feedback_response_label' })}
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {message.content || message.text}
              </Typography>
            </Box>
          ) : null}
        </Box>

        {FEEDBACK_QUESTION_GROUPS.map(group => (
          <FeedbackChoiceGroup
            key={group.id}
            group={group}
            selectedValue={selections[group.id]}
            onSelect={handleSelectChoice}
            disabled={isSubmitting}
            styles={styles}
          />
        ))}

        <TextField
          fullWidth
          multiline
          minRows={3}
          maxRows={8}
          label={intl.formatMessage({ id: 'command_intelligence_feedback_input_label' })}
          placeholder={intl.formatMessage({ id: 'command_intelligence_feedback_input_placeholder' })}
          value={feedback}
          onChange={event => setFeedback(event.target.value)}
          disabled={isSubmitting}
          sx={{ mt: 1 }}
        />

        {error ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        ) : null}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={isSubmitting}>
          {intl.formatMessage({ id: 'cancel' })}
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={isSubmitting}>
          {isSubmitting ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            intl.formatMessage({ id: 'command_intelligence_feedback_submit' })
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FeedbackDialog;
